import {
  createSignalFromObservable,
  type Signal,
} from '@blocksuite/affine/shared/utils';
import { LiveData, Service } from '@toeverything/infra';
import { map } from 'rxjs';

import type { GlobalStateService } from '../../storage';

const AI_TOOLS_CONFIG_KEY = 'AIToolsConfig';

export interface AIToolsConfig {
  searchWorkspace?: boolean;
  readingDocs?: boolean;
  retrievalMode?: AIRetrievalMode;
  agentMode?: AIAgentMode;
  confirmationProfile?: AIConfirmationProfile;
  /** Surface capability. Canvas turn nodes intentionally disable durable Targets. */
  targetMode?: 'enabled' | 'disabled';
  /** One-turn decision; never persisted by AIToolsConfigService. */
  targetRoutingDecision?: 'accepted' | 'declined';
  /** @deprecated Compatibility input for clients predating Target terminology. */
  goalMode?: 'enabled' | 'disabled';
}

export type AIRetrievalMode =
  | 'auto'
  | 'off'
  | 'workspace'
  | 'web'
  | 'workspace-web';

export type AIAgentMode = 'chat' | 'agent';

export type AIConfirmationProfile = 'confirm_each' | 'policy' | 'autonomous';

type LegacyAIAgentMode = AIAgentMode | 'auto' | 'on' | 'off';

function normalizeRetrievalMode(
  mode: AIRetrievalMode | undefined
): AIRetrievalMode {
  if (mode === 'workspace-web') return 'auto';
  if (mode === 'workspace') return 'off';
  return mode ?? 'auto';
}

function normalizeAgentMode(mode: LegacyAIAgentMode | undefined): AIAgentMode {
  return mode === 'agent' || mode === 'on' ? 'agent' : 'chat';
}

function normalizeConfirmationProfile(
  profile: AIConfirmationProfile | undefined
): AIConfirmationProfile {
  return profile === 'confirm_each' || profile === 'autonomous'
    ? profile
    : 'policy';
}

export class AIToolsConfigService extends Service {
  constructor(private readonly globalStateService: GlobalStateService) {
    super();

    const { signal, cleanup: enabledCleanup } =
      createSignalFromObservable<AIToolsConfig>(this.config$, {
        searchWorkspace: false,
        readingDocs: false,
        retrievalMode: 'auto',
        agentMode: 'chat',
        confirmationProfile: 'policy',
      });
    this.config = signal;
    this.disposables.push(enabledCleanup);
  }

  config: Signal<AIToolsConfig>;

  private readonly config$ = LiveData.from(
    this.globalStateService.globalState.watch<AIToolsConfig>(
      AI_TOOLS_CONFIG_KEY
    ),
    undefined
  ).pipe(
    map(config => ({
      searchWorkspace: false,
      readingDocs: false,
      retrievalMode: normalizeRetrievalMode(config?.retrievalMode),
      agentMode: normalizeAgentMode(config?.agentMode as LegacyAIAgentMode),
      confirmationProfile: normalizeConfirmationProfile(
        config?.confirmationProfile
      ),
    }))
  );

  setConfig = (data: Partial<AIToolsConfig>) => {
    this.globalStateService.globalState.set(AI_TOOLS_CONFIG_KEY, {
      ...this.config.value,
      ...data,
    });
  };
}
